import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useGetJobsMutation } from "slice/jobs";
import { FilterBarInputs } from "./types";

export const initialFilterBarInputs: FilterBarInputs = {
    gender: "Male",
    min_age: 18,
    max_age: 70,
    sort_by: "most_recent",
};

export const useFindTalent = () => {
    const [inputs, setInputs] = useState<FilterBarInputs>(
        initialFilterBarInputs
    );
    const [searchParams, setSearchParams] = useSearchParams();
    const [getUsers, { data, isLoading: isFetching }] = useGetJobsMutation();

    const handleInputChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const { name, value } = event.target;
            setInputs((prevValues) => ({
                ...prevValues,
                [name]: value,
            }));
            setSearchParams({
                [name]: value,
                sort_by: inputs.sort_by,
                min_age: inputs.min_age.toString(),
                max_age: inputs.max_age.toString(),
            });
        },
        [inputs.max_age, inputs.min_age, inputs.sort_by, setSearchParams]
    );

    const handleSortByChange = useCallback(
        (value: any) => {
            setInputs((prevValues) => ({
                ...prevValues,
                sort_by: value,
            }));
            setSearchParams({
                gender: inputs.gender,
                sort_by: value,
                min_age: inputs.min_age.toString(),
                max_age: inputs.max_age.toString(),
            });
            handleApply({
                ...inputs,
                sort_by: value,
            });
        },
        [inputs.gender, inputs.max_age, inputs.min_age, setSearchParams]
    );

    const handleAgeRangeSelector = useCallback(
        (min: number, max: number) => {
            setInputs((prevValues) => ({
                ...prevValues,
                min_age: min,
                max_age: max,
            }));
            setSearchParams({
                gender: inputs.gender,
                sort_by: inputs.sort_by,
                min_age: min.toString(),
                max_age: max.toString(),
            });
        },
        [inputs.gender, inputs.sort_by, setSearchParams]
    );

    const handleApply = (currentInputs?: FilterBarInputs) => {
        getUsers(currentInputs ? currentInputs : inputs);
    };

    const handleReset = () => {
        if (
            !(
                inputs.gender === initialFilterBarInputs.gender &&
                inputs.min_age === initialFilterBarInputs.min_age &&
                inputs.max_age === initialFilterBarInputs.max_age &&
                inputs.sort_by === initialFilterBarInputs.sort_by
            )
        ) {
            setInputs(initialFilterBarInputs);
            getUsers(initialFilterBarInputs);
        }
    };

    useEffect(() => {
        getUsers(inputs);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getUsers]);

    return {
        users: data?.users ?? [],
        count: data?.count ?? 0,
        isFetching,
        inputs,
        handleInputChange,
        handleAgeRangeSelector,
        handleApply,
        handleReset,
        handleSortByChange,
    };
};
